<template>
  <div class="activation-stage-form">
    <div class="progress-header">
      <div class="flex items-center justify-end">
        <span class="text-xs text-gray-500">Progress</span>
        <div class="w-32 bg-gray-200 rounded-full h-2 mx-2">
          <div
            class="bg-gradient-to-r from-emerald-500 to-emerald-600 h-2 rounded-full transition-all duration-500"
            :style="`width: ${progressPercentage}%`" />
        </div>
        <span class="text-xs font-medium text-gray-700">{{ progressPercentage }}%</span>
      </div>
    </div>

    <div
      v-if="loading"
      class="content-body">
      <div class="flex items-center justify-center py-8">
        <div class="text-center">
          <i class="fas fa-spinner fa-spin text-3xl text-emerald-600 mb-2" />
          <p class="text-sm text-gray-500">
            Loading activation stage...
          </p>
        </div>
      </div>
    </div>

    <div
      v-else
      class="content-body">
      <!-- View Mode -->
      <div v-if="!isEditing">
        <div class="space-y-4">
          <!-- Section 1: Call Recording & Specialist -->
          <div class="section">
            <h6 class="section-title">
              <i class="fas fa-phone-alt text-gray-500 mr-2" />
              Initial Contact
            </h6>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="info-item">
                <div class="info-label">
                  <i class="fas fa-microphone mr-1.5" />
                  Activation Call Recording
                </div>
                <div v-if="activationStage.activationCallRecordingUrl">
                  <a
                    :href="activationStage.activationCallRecordingUrl"
                    target="_blank"
                    class="info-link">
                    <i class="fas fa-play-circle mr-1.5" />
                    Listen to Recording
                    <i class="fas fa-external-link-alt ml-1 text-xs" />
                  </a>
                </div>
                <div
                  v-else
                  class="text-sm text-gray-400">
                  Not recorded
                </div>
              </div>

              <div class="info-item">
                <div class="info-label">
                  <i class="fas fa-user-tie mr-1.5" />
                  Activation Specialist
                </div>
                <div class="info-value">
                  {{ activationStage.activationSpecialistUser || 'Not assigned' }}
                </div>
              </div>
            </div>

            <div
              v-if="activationStage.activationTranscript"
              class="mt-3">
              <div class="info-label mb-2">
                <i class="fas fa-file-alt mr-1.5" />
                Call Transcript
              </div>
              <div class="transcript-box">
                {{ activationStage.activationTranscript }}
              </div>
            </div>
          </div>

          <!-- Section 2: Welcome & Forms -->
          <div class="section">
            <h6 class="section-title">
              <i class="fas fa-envelope text-gray-500 mr-2" />
              Welcome & Forms
            </h6>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="info-item">
                <div class="info-label">
                  <i class="fas fa-paper-plane mr-1.5" />
                  Post Sales Email
                </div>
                <div v-if="activationStage.welcomeEmailSentAt">
                  <div class="text-sm text-gray-700">
                    Sent {{ formatDate(activationStage.welcomeEmailSentAt) }}
                  </div>
                  <div class="text-xs text-gray-500 mt-0.5">
                    Template: {{ activationStage.welcomeEmailTemplate || 'Standard' }}
                  </div>
                </div>
                <div
                  v-else
                  class="text-sm text-gray-400">
                  Not sent
                </div>
              </div>

              <div class="info-item">
                <div class="info-label">
                  <i class="fas fa-clipboard-check mr-1.5" />
                  Jot Form
                </div>
                <div v-if="activationStage.activationFormSubmitted">
                  <span class="text-sm text-emerald-600 font-medium">
                    <i class="fas fa-check-circle mr-1" />
                    Submitted
                  </span>
                  <a
                    v-if="activationStage.activationFormUrl"
                    :href="activationStage.activationFormUrl"
                    target="_blank"
                    class="info-link ml-2">
                    View Form
                  </a>
                </div>
                <div
                  v-else
                  class="text-sm text-amber-600">
                  <i class="fas fa-clock mr-1" />
                  Pending submission
                </div>
              </div>
            </div>
          </div>

          <!-- Section 3: Google Profile -->
          <div class="section">
            <h6 class="section-title">
              <i class="fab fa-google text-gray-500 mr-2" />
              Google Profile Authentication
            </h6>
            <div class="info-item">
              <div v-if="activationStage.googleProfileAuthenticated">
                <span class="text-sm text-emerald-600 font-medium">
                  <i class="fas fa-check-circle mr-1" />
                  Authenticated
                </span>
                <span
                  v-if="activationStage.googleProfileMethod"
                  class="text-sm text-gray-600 ml-2">
                  via {{ activationStage.googleProfileMethod }}
                </span>
              </div>
              <div
                v-else
                class="text-sm text-amber-600">
                <i class="fas fa-exclamation-circle mr-1" />
                Not authenticated
              </div>
            </div>
          </div>

          <!-- Section 4: Client Data Checklist -->
          <div class="section">
            <h6 class="section-title">
              <i class="fas fa-tasks text-gray-500 mr-2" />
              Client Data Intake Checklist
            </h6>
            <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div class="checklist-item">
                <i
                  :class="[
                    'fas mr-2',
                    activationStage.napHoursCompleted ? 'fa-check-square text-emerald-600' : 'fa-square text-gray-400'
                  ]" />
                <span :class="activationStage.napHoursCompleted ? 'text-gray-700' : 'text-gray-500'">
                  NAP + Hours
                </span>
              </div>
              <div class="checklist-item">
                <i
                  :class="[
                    'fas mr-2',
                    activationStage.categoriesServicesCompleted ? 'fa-check-square text-emerald-600' : 'fa-square text-gray-400'
                  ]" />
                <span :class="activationStage.categoriesServicesCompleted ? 'text-gray-700' : 'text-gray-500'">
                  Categories + Services
                </span>
              </div>
              <div class="checklist-item">
                <i
                  :class="[
                    'fas mr-2',
                    activationStage.serviceAreaCompleted ? 'fa-check-square text-emerald-600' : 'fa-square text-gray-400'
                  ]" />
                <span :class="activationStage.serviceAreaCompleted ? 'text-gray-700' : 'text-gray-500'">
                  Service Area/Zip Codes
                </span>
              </div>
              <div class="checklist-item">
                <i
                  :class="[
                    'fas mr-2',
                    activationStage.logoBrandAssetsCompleted ? 'fa-check-square text-emerald-600' : 'fa-square text-gray-400'
                  ]" />
                <span :class="activationStage.logoBrandAssetsCompleted ? 'text-gray-700' : 'text-gray-500'">
                  Logo + Brand Assets
                </span>
              </div>
            </div>
          </div>

          <!-- Section 5: Post Activation -->
          <div class="section">
            <h6 class="section-title">
              <i class="fas fa-forward text-gray-500 mr-2" />
              Post Activation
            </h6>
            <div
              v-if="activationStage.transitionNotes"
              class="mt-3">
              <div class="info-label mb-2">
                <i class="fas fa-sticky-note mr-1.5" />
                Post Activation Notes
              </div>
              <div class="notes-box">
                {{ activationStage.transitionNotes }}
              </div>
            </div>
          </div>
        </div>

        <div class="mt-6">
          <button
            class="action-button action-button-primary"
            @click="startEditing">
            <i class="fas fa-edit mr-1.5" />
            Edit Activation Stage
          </button>
        </div>
      </div>

      <!-- Edit Mode -->
      <form
        v-else
        class="space-y-6"
        @submit.prevent="saveActivationStage">
        <!-- Section 1: Initial Contact -->
        <div class="form-section">
          <h6 class="form-section-title">
            <i class="fas fa-phone-alt text-gray-500 mr-2" />
            Initial Contact
          </h6>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="form-group">
              <label class="form-label">Activation Call Recording URL</label>
              <input
                v-model="form.activation_call_recording_url"
                type="url"
                class="form-input"
                placeholder="https://example.com/recording">
            </div>
            <div class="form-group">
              <label class="form-label">Activation Specialist</label>
              <select
                v-model="form.activation_specialist_id"
                class="form-input">
                <option value="">
                  Select specialist
                </option>
                <option
                  v-for="member in teamMembers"
                  :key="member.id"
                  :value="member.id">
                  {{ member.displayName }}
                </option>
              </select>
            </div>
          </div>
          <div class="form-group">
            <label class="form-label">Call Transcript</label>
            <textarea
              v-model="form.activation_transcript"
              rows="4"
              class="form-input"
              placeholder="Enter call transcript or key notes..." />
          </div>
        </div>

        <!-- Section 2: Welcome & Forms -->
        <div class="form-section">
          <h6 class="form-section-title">
            <i class="fas fa-envelope text-gray-500 mr-2" />
            Welcome & Forms
          </h6>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="form-group">
              <label class="form-label">Post Sales Email Sent Date</label>
              <input
                v-model="form.welcome_email_sent_at"
                type="datetime-local"
                class="form-input">
            </div>
            <div class="form-group">
              <label class="form-label">Welcome Email Template</label>
              <select
                v-model="form.welcome_email_template"
                class="form-input">
                <option value="">
                  Select template
                </option>
                <option
                  v-for="(label, value) in emailTemplates"
                  :key="value"
                  :value="label">
                  {{ label }}
                </option>
              </select>
            </div>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="form-group">
              <label class="form-label flex items-center">
                <input
                  v-model="form.activation_form_submitted"
                  type="checkbox"
                  class="mr-2 text-emerald-600 rounded">
                Jot Form Submitted
              </label>
            </div>
            <div class="form-group">
              <label class="form-label">Citation Client Form URL</label>
              <input
                v-model="form.activation_form_url"
                type="url"
                class="form-input"
                placeholder="https://...">
            </div>
          </div>
        </div>

        <!-- Section 3: Google Profile -->
        <div class="form-section">
          <h6 class="form-section-title">
            <i class="fab fa-google text-gray-500 mr-2" />
            Google Profile Authentication
          </h6>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="form-group">
              <label class="form-label flex items-center">
                <input
                  v-model="form.google_profile_authenticated"
                  type="checkbox"
                  class="mr-2 text-emerald-600 rounded">
                Google Profile Authenticated
              </label>
            </div>
            <div class="form-group">
              <label class="form-label">Authentication Method</label>
              <select
                v-model="form.google_profile_method"
                class="form-input"
                :disabled="!form.google_profile_authenticated">
                <option value="">
                  Select method
                </option>
                <option
                  v-for="(label, value) in googleMethods"
                  :key="value"
                  :value="label">
                  {{ label }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <!-- Section 4: Client Data Checklist -->
        <div class="form-section">
          <h6 class="form-section-title">
            <i class="fas fa-tasks text-gray-500 mr-2" />
            Client Data Intake Checklist
          </h6>
          <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
            <label class="checklist-label">
              <input
                v-model="form.nap_hours_completed"
                type="checkbox"
                class="mr-2 text-emerald-600 rounded">
              NAP + Hours
            </label>
            <label class="checklist-label">
              <input
                v-model="form.categories_services_completed"
                type="checkbox"
                class="mr-2 text-emerald-600 rounded">
              Categories + Services
            </label>
            <label class="checklist-label">
              <input
                v-model="form.service_area_completed"
                type="checkbox"
                class="mr-2 text-emerald-600 rounded">
              Service Area/Zip Codes
            </label>
            <label class="checklist-label">
              <input
                v-model="form.logo_brand_assets_completed"
                type="checkbox"
                class="mr-2 text-emerald-600 rounded">
              Logo + Brand Assets
            </label>
          </div>
        </div>

        <!-- Section 5: Post Activation -->
        <div class="form-section">
          <h6 class="form-section-title">
            <i class="fas fa-forward text-gray-500 mr-2" />
            Post Activation
          </h6>
          <div class="form-group">
            <label class="form-label">Post Activation Notes</label>
            <textarea
              v-model="form.transition_notes"
              rows="4"
              class="form-input"
              placeholder="Important notes for the onboarding coordinator..." />
          </div>
        </div>

        <div class="form-actions-sticky">
          <div class="form-actions">
            <div class="flex gap-2">
              <button
                type="submit"
                :disabled="saving"
                class="action-button action-button-success">
                <i
                  v-if="saving"
                  class="fas fa-spinner fa-spin mr-1.5" />
                <i
                  v-else
                  class="fas fa-save mr-1.5" />
                Save
              </button>
              <button
                type="button"
                :disabled="saving"
                class="action-button action-button-secondary"
                @click="cancelEditing">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import mdtoast from '@dmuy/toast'
import axios from '../init/axios'

const props = defineProps({
  organizationId: {
    type: String,
    required: true
  }
})

// State
const loading = ref(true)
const saving = ref(false)
const isEditing = ref(false)
const activationStage = ref({})
const teamMembers = ref([])
const form = ref({
  activation_call_recording_url: '',
  activation_transcript: '',
  activation_specialist: '',
  activation_specialist_id: '',
  welcome_email_sent_at: '',
  welcome_email_template: '',
  activation_form_submitted: false,
  activation_form_url: '',
  google_profile_authenticated: false,
  google_profile_method: '',
  nap_hours_completed: false,
  categories_services_completed: false,
  service_area_completed: false,
  logo_brand_assets_completed: false,
  transition_notes: ''
})

// Constants
const emailTemplates = {
  standard: 'Standard Welcome',
  premium: 'Premium Welcome',
  enterprise: 'Enterprise Welcome',
  custom: 'Custom Template'
}

const googleMethods = {
  email_verification: 'Email Verification',
  sms_verification: 'SMS Verification',
  postcard: 'Postcard',
  instant: 'Instant Verification',
  manual: 'Manual Process'
}

// Computed
const progressPercentage = computed(() => {
  if (!activationStage.value) return 0

  const totalItems = 9
  let completedItems = 0

  if (activationStage.value.activationCallRecordingUrl) completedItems += 1
  if (activationStage.value.activationSpecialistId) completedItems += 1
  if (activationStage.value.welcomeEmailSentAt) completedItems += 1
  if (activationStage.value.activationFormSubmitted) completedItems += 1
  if (activationStage.value.googleProfileAuthenticated) completedItems += 1
  if (activationStage.value.napHoursCompleted) completedItems += 1
  if (activationStage.value.categoriesServicesCompleted) completedItems += 1
  if (activationStage.value.serviceAreaCompleted) completedItems += 1
  if (activationStage.value.logoBrandAssetsCompleted) completedItems += 1

  return Math.round((completedItems / totalItems) * 100)
})

// Methods
const fetchActivationStage = async() => {
  try {
    loading.value = true
    const response = await axios.get(`/ajax/organizations/${props.organizationId}/activation_stage`)
    if (response.data.success) {
      activationStage.value = response.data.activationStage || {}
      teamMembers.value = response.data.teamMembers || []
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching activation stage:', error)
    mdtoast.error('Failed to load activation stage', { duration: 3000 })
  } finally {
    loading.value = false
  }
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const startEditing = () => {
  const stage = activationStage.value
  form.value = {
    activation_call_recording_url: stage.activationCallRecordingUrl || '',
    activation_transcript: stage.activationTranscript || '',
    activation_specialist: stage.activationSpecialist || '',
    activation_specialist_id: stage.activationSpecialistId || '',
    welcome_email_sent_at: stage.welcomeEmailSentAt ? stage.welcomeEmailSentAt.slice(0, 16) : '',
    welcome_email_template: stage.welcomeEmailTemplate || '',
    activation_form_submitted: stage.activationFormSubmitted || false,
    activation_form_url: stage.activationFormUrl || '',
    google_profile_authenticated: stage.googleProfileAuthenticated || false,
    google_profile_method: stage.googleProfileMethod || '',
    nap_hours_completed: stage.napHoursCompleted || false,
    categories_services_completed: stage.categoriesServicesCompleted || false,
    service_area_completed: stage.serviceAreaCompleted || false,
    logo_brand_assets_completed: stage.logoBrandAssetsCompleted || false,
    transition_notes: stage.transitionNotes || ''
  }
  isEditing.value = true
}

const cancelEditing = () => {
  isEditing.value = false
}

const saveActivationStage = async() => {
  try {
    saving.value = true
    const payload = { activation_stage: form.value }
    const response = await axios.patch(
      `/ajax/organizations/${props.organizationId}/activation_stage`,
      payload
    )

    if (response.data.success) {
      // eslint-disable-next-line no-console
      console.log('Response data:', response.data.activationStage)
      activationStage.value = response.data.activationStage
      isEditing.value = false
      mdtoast.success('Activation stage saved successfully', { duration: 3000 })
    } else {
      mdtoast.error(response.data.error || 'Failed to save activation stage', { duration: 3000 })
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error saving activation stage:', error)
    mdtoast.error('Failed to save activation stage', { duration: 3000 })
  } finally {
    saving.value = false
  }
}

// Lifecycle
onMounted(() => {
  fetchActivationStage()
})
</script>

<style scoped>
.activation-stage-form {
  display: flex;
  flex-direction: column;
}

.progress-header {
  @apply mb-4;
}

.content-body {
  overflow-y: auto;
  flex: 1;
}

.section {
  @apply bg-gray-50 rounded-lg p-4 space-y-3;
}

.section-title {
  @apply text-sm font-semibold text-gray-700 mb-3 flex items-center;
}

.info-grid {
  @apply grid grid-cols-1 md:grid-cols-2 gap-4;
}

.info-item {
  @apply space-y-1;
}

.info-label {
  @apply text-xs font-semibold text-gray-600 uppercase tracking-wider flex items-center;
}

.info-value {
  @apply text-sm text-gray-900;
}

.info-link {
  @apply text-sm text-indigo-600 hover:text-indigo-800 transition-colors
         inline-flex items-center hover:underline;
}

.transcript-box {
  @apply bg-white rounded-md p-3 text-sm text-gray-700 whitespace-pre-wrap border border-gray-200;
}

.notes-box {
  @apply bg-white rounded-md p-3 text-sm text-gray-700 whitespace-pre-wrap border border-gray-200;
}

.checklist-item {
  @apply flex items-center text-sm;
}

.form-section {
  @apply bg-gray-50 rounded-lg p-4 space-y-4;
}

.form-section-title {
  @apply text-sm font-semibold text-gray-700 mb-3 flex items-center;
}

.form-group {
  @apply space-y-1;
}

.form-label {
  @apply block text-sm font-medium text-gray-700;
}

.form-input {
  @apply w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
         focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500
         transition-colors duration-200;
}

.checklist-label {
  @apply flex items-center text-sm text-gray-700 cursor-pointer hover:text-gray-900;
}

.form-actions-sticky {
  position: sticky;
  bottom: 0;
  background-color: white;
  margin-top: 1.5rem;
  margin-left: -1.25rem;
  margin-right: -1.25rem;
  margin-bottom: -1.25rem;
  padding: 0 1.25rem;
  box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.05);
}

.form-actions {
  @apply flex items-center justify-between py-4 border-t border-gray-200;
}

.action-button {
  @apply inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg
         transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2
         disabled:opacity-50 disabled:cursor-not-allowed;
}

.action-button-primary {
  @apply bg-emerald-600 text-white hover:bg-emerald-700 focus:ring-emerald-500;
}

.action-button-success {
  @apply bg-green-600 text-white hover:bg-green-700 focus:ring-green-500;
}

.action-button-secondary {
  @apply bg-gray-100 text-gray-700 hover:bg-gray-200 focus:ring-gray-500;
}
</style>
